import { z } from "zod";

/*
 * Charts and widgets are data, never code: a spec is validated here and rendered by
 * libs/widgets. Every kind that shows numbers must carry a source (the "no source, no chart"
 * rule); only the qualitative comparison may go without one.
 */

const text = (max: number) => z.string().trim().min(1).max(max);

export const widgetSourceSchema = z.object({
  label: text(140).describe("Publisher and title, e.g. 'ABS, Labour Force Survey, Sept 2026'"),
  url: z
    .string()
    .url()
    .refine((value) => value.startsWith("https://"), "Source must be an https URL"),
});
export type WidgetSource = z.infer<typeof widgetSourceSchema>;

const common = {
  title: text(120),
  subtitle: text(200).optional(),
  /** Caveats readers should see, e.g. sample size or "Australian respondents only". */
  note: text(240).optional(),
};

const value = z.number().finite();

export const barWidgetSchema = z.object({
  kind: z.literal("bar"),
  ...common,
  unit: text(16).optional().describe("Suffix for values, e.g. '%' or ' hrs'"),
  items: z
    .array(z.object({ label: text(60), value }))
    .min(2)
    .max(12),
  /** Label of the bar to emphasise, if any. */
  highlight: text(60).optional(),
  source: widgetSourceSchema,
});

export const lineWidgetSchema = z
  .object({
    kind: z.literal("line"),
    ...common,
    unit: text(16).optional(),
    xLabels: z.array(text(24)).min(2).max(24),
    series: z
      .array(z.object({ name: text(40), values: z.array(value) }))
      .min(1)
      .max(4),
    source: widgetSourceSchema,
  })
  .refine((spec) => spec.series.every((s) => s.values.length === spec.xLabels.length), {
    message: "Every series needs one value per x label",
  });

export const donutWidgetSchema = z.object({
  kind: z.literal("donut"),
  ...common,
  unit: text(16).optional(),
  segments: z
    .array(z.object({ label: text(40), value: value.nonnegative() }))
    .min(2)
    .max(6),
  source: widgetSourceSchema,
});

/** A small dashboard of headline figures. */
export const statsWidgetSchema = z.object({
  kind: z.literal("stats"),
  ...common,
  items: z
    .array(
      z.object({
        value: text(16).describe("Display value, e.g. '34%' or '2,018'"),
        label: text(90),
      }),
    )
    .min(2)
    .max(4),
  source: widgetSourceSchema,
});

export const tableWidgetSchema = z
  .object({
    kind: z.literal("table"),
    ...common,
    columns: z.array(text(40)).min(2).max(6),
    rows: z
      .array(z.array(text(80)))
      .min(1)
      .max(12),
    source: widgetSourceSchema,
  })
  .refine((spec) => spec.rows.every((row) => row.length === spec.columns.length), {
    message: "Every row needs one cell per column",
  });

/** Qualitative side-by-side (e.g. weak vs strong answer). No numbers, so no source required. */
export const comparisonWidgetSchema = z.object({
  kind: z.literal("comparison"),
  ...common,
  left: z.object({ heading: text(40), points: z.array(text(200)).min(1).max(6) }),
  right: z.object({ heading: text(40), points: z.array(text(200)).min(1).max(6) }),
  /** Which side is the recommended one, if any. */
  recommended: z.enum(["left", "right"]).optional(),
});

export const widgetSpecSchema = z.discriminatedUnion("kind", [
  barWidgetSchema,
  lineWidgetSchema,
  donutWidgetSchema,
  statsWidgetSchema,
  tableWidgetSchema,
  comparisonWidgetSchema,
]);

export type WidgetSpec = z.infer<typeof widgetSpecSchema>;
export type WidgetKind = WidgetSpec["kind"];

/** Kinds that display numbers and therefore must cite a source. */
export const SOURCED_WIDGET_KINDS: WidgetKind[] = ["bar", "line", "donut", "stats", "table"];
